import { type RefObject, useLayoutEffect } from 'react'

/** How long the chat must stop changing, once the next question arrived, before the held room is let go. */
const SETTLE_MS = 500
/** The same, while nothing new has come in: an answer usually brings its next question by then. */
const WAIT_MS = 1500

/**
 * Keeps a chat from getting shorter while an answer is in flight: when content leaves its bottom (an answered
 * question's form), `spacer` takes up the height, so nothing above slides down. Once what comes next has arrived and
 * settled, the held room slides away, so the latest question sits at the bottom instead of above a gap the size of a
 * tall form that left.
 */
export function useChatHoldHeight(
  scroller: RefObject<HTMLElement | null>,
  content: RefObject<HTMLElement | null>,
  spacer: RefObject<HTMLElement | null>
) {
  useLayoutEffect(() => {
    const scrollNode = scroller.current
    const contentNode = content.current
    const spacerNode = spacer.current
    if (!scrollNode || !contentNode || !spacerNode) return

    let floor = contentNode.offsetHeight
    let width = contentNode.offsetWidth
    let scrollTop = scrollNode.scrollTop
    let lastHeight = floor
    // Something new came in under the held room since the hold began.
    let arrived = false
    let release: ReturnType<typeof setTimeout> | undefined
    const onScroll = () => {
      scrollTop = scrollNode.scrollTop
    }
    const letGo = () => {
      arrived = false
      floor = contentNode.offsetHeight
      spacerNode.style.transition = 'height 300ms ease-out'
      spacerNode.style.height = '0px'
    }

    const observer = new ResizeObserver(() => {
      const height = contentNode.offsetHeight
      // A new width reflows the text, which isn't content leaving, so the hold starts over.
      if (contentNode.offsetWidth !== width) {
        width = contentNode.offsetWidth
        floor = height
      }
      if (height > lastHeight) arrived = true
      lastHeight = height
      floor = Math.max(floor, height)
      spacerNode.style.transition = 'none'
      spacerNode.style.height = `${floor - height}px`
      // The shrink already clamped the scroll before this ran; put it back where the reader left it.
      if (scrollNode.scrollTop < scrollTop) scrollNode.scrollTop = scrollTop
      scrollTop = scrollNode.scrollTop
      // Let the held room go once the chat stops changing: soon after the next question came in, later while the
      // answer may still be on its way.
      clearTimeout(release)
      if (floor <= height) arrived = false
      else release = setTimeout(letGo, arrived ? SETTLE_MS : WAIT_MS)
    })
    observer.observe(contentNode)
    scrollNode.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      clearTimeout(release)
      observer.disconnect()
      scrollNode.removeEventListener('scroll', onScroll)
    }
  }, [scroller, content, spacer])
}
